import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getFAQs, createFAQ, updateFAQ, deleteFAQ } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function AdminFAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    active: true,
  });

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      const res = await getFAQs(false);
      setFaqs(res.data);
    } catch (err) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingFaq) {
        await updateFAQ(editingFaq.id, formData);
        toast.success('FAQ updated');
      } else {
        await createFAQ({ ...formData, order: faqs.length });
        toast.success('FAQ added');
      }
      loadFAQs();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order || 0,
      active: faq.active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await deleteFAQ(id);
      toast.success('FAQ deleted');
      loadFAQs();
    } catch (err) {
      toast.error('Failed to delete FAQ');
    }
  };

  const toggleActive = async (faq) => {
    try {
      await updateFAQ(faq.id, { ...faq, active: !faq.active });
      toast.success(faq.active ? 'FAQ hidden' : 'FAQ visible');
      loadFAQs();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const resetForm = () => {
    setEditingFaq(null);
    setFormData({ question: '', answer: '', order: 0, active: true });
  };

  return (
    <AdminLayout title="FAQ Management">
      <div className="space-y-6" data-testid="admin-faq">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">FAQ</h1>
            <p className="text-[#888] text-sm">Manage frequently asked questions</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-faq-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add FAQ
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingFaq ? 'Edit FAQ' : 'Add FAQ'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="faq-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Question *</label>
                  <input
                    type="text"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="What is your question?"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Answer *</label>
                  <textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    required
                    rows={5}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none resize-none"
                    placeholder="Provide a helpful answer..."
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 accent-[#ff5a1f]"
                  />
                  <label htmlFor="active" className="text-white text-sm">Show on website</label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingFaq ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : faqs.length > 0 ? (
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={faq.id}
                className={`bg-[#0A0A0A] border p-6 ${
                  faq.active ? 'border-[#1F1F1F]' : 'border-[#1F1F1F] opacity-60'
                }`}
                data-testid={`faq-item-${faq.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-[#333] cursor-move">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-syne font-bold text-white">{faq.question}</h3>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {faq.active ? (
                          <span className="text-green-500 text-xs flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Visible
                          </span>
                        ) : (
                          <span className="text-[#555] text-xs flex items-center gap-1">
                            <EyeOff className="w-3 h-3" /> Hidden
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-[#888] text-sm line-clamp-2">{faq.answer}</p>
                  </div>
                  
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleActive(faq)}
                      className="text-[#888] hover:text-white p-2"
                      title={faq.active ? 'Hide' : 'Show'}
                    >
                      {faq.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(faq)}
                      className="text-[#888] hover:text-[#ff5a1f] p-2"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="text-[#888] hover:text-red-500 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No FAQs yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Add your first FAQ
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
